import { useCallback } from "react";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { actAsCacheKey, getActAsOrgId } from "../contexts/actAsStore";
import { useProfileContext } from "../contexts/ProfileContext";
import { useApiClient } from "../data/clients/ApiClient";
import {
  StorageFreshnessResponseDto,
  VerifyStorageRowDto,
} from "../data/models/responses/VerifyStorageResponseDto";
import { enviroment } from "../enviroment";
import { CartItem } from "../presentation/screens/order-create/models/CartItem";
import { collectCartLogoIds } from "../presentation/screens/order-confirmation/storageVerify";
import {
  patchStorageInDesignQueryCache,
  patchStorageInQueryCache,
  requestedStore,
  resolveStorageFreshnessOrgId,
  rowInsideManualWindow,
  STORAGE_FRESHNESS_MAX_IDS,
  STORAGE_FRESHNESS_REQUEST_TTL_MS,
  STORAGE_FRESHNESS_TIMEOUT_MS,
  STORAGE_MANUAL_BACKOFF_MS,
} from "../utils/functions/storageFreshness";
import {
  alignGuardWithStamp,
  beginManualRefresh,
  endManualRefresh,
  getManualRefreshLock,
  isManualRefreshInFlight,
  pauseManualRefresh,
} from "./useManualStorageRefresh";
import { landFreshnessResponse } from "./useStorageFreshness";

// Stock look-up on add-to-cart (flag storageRefreshOnAddToCartEnabled;
// Nichlas 8/9: "when something is put in the cart, can it do a quick
// look-up without hurting performance or the user?"). The cart line is added
// at once with the count the page already knows; what follows here is ONE
// non-blocking forced storage-freshness call for exactly the logos just
// added, whose answer lands in the cart's own copy of the logo and in the
// react-query cache a moment later. Silence on every outcome: the customer
// asked for a cart line, not for a look-up.

type Refresh = (
  orgId: number,
  logoIds: number[],
  signal?: AbortSignal,
  options?: { force?: boolean }
) => Promise<StorageFreshnessResponseDto>;

/**
 * Lands the verified rows in the cart's own copies of the logos. `scope` is
 * the act-as scope the call was made in: the cart of THAT identity is the
 * one the rows belong to - an admin may have switched act-as meanwhile.
 */
export type ApplyStorageRows = (
  rows: VerifyStorageRowDto[],
  scope: number
) => void;

export interface AddToCartRefreshDeps {
  refresh: Refresh;
  queryClient: QueryClient;
  applyRows: ApplyStorageRows;
}

/**
 * ONE forced storage-freshness call for the logos just added to the cart -
 * or none. Never called on render or from an effect: only an add costs a
 * call. Skipped, with nothing marked, while every button is paused (the API
 * said force is unavailable / a call just failed) and while another forced
 * call is in the air (one per page at a time, like the button). Logos asked
 * for within the 5-minute window - by this look-up, the on-view call or a
 * click - are dropped (a second add of the same logo costs nothing); what
 * is left is marked in the shared requested store BEFORE the call leaves
 * and capped at the API's batch size. The answer is landed like every other
 * freshness answer (landFreshnessResponse: API-disabled pause, max age,
 * logo cache patch in the act-as scope of the call), then the design detail
 * entries and, through `applyRows`, the cart's own copies. A forced answer
 * the API gave from its window (rowInsideManualWindow: the row not verified
 * in this call but stamped from NAV less than 5 minutes ago - another tab's
 * click, the on-view call, a colleague) holds the verified figure of a
 * moment ago and lands the same way, in the same silence - no toast, the
 * customer asked for a cart line. After a landed row the requested-store
 * guard is aligned with its stamp (alignGuardWithStamp), so the button of
 * that logo opens when the row's window closes, like after a click. Rows the
 * API really could not verify (budget, latch, NAV degraded - their stamp
 * hours old or never set) are left alone in silence, nothing paused. An
 * answer without forced=true and without a verified row means the API's
 * manual flag is off: every forced caller pauses 5 minutes, one probe per
 * window. Transport trouble or the 8 s timeout pauses them
 * STORAGE_MANUAL_BACKOFF_MS. Returns whether a call left.
 */
export function requestStorageRefreshOnAddToCart(
  orgId: number,
  logoIds: readonly number[],
  deps: AddToCartRefreshDeps
): boolean {
  if (!(orgId > 0)) return false;
  const now = Date.now();
  if (now < getManualRefreshLock().untilMs) return false;
  if (isManualRefreshInFlight()) return false;
  const ids: number[] = [];
  logoIds.forEach((id) => {
    if (!(id > 0) || ids.includes(id)) return;
    if (requestedStore.has(orgId, id, now)) return;
    ids.push(id);
  });
  if (ids.length === 0) return false;
  const batch = ids.slice(0, STORAGE_FRESHNESS_MAX_IDS);
  requestedStore.mark(orgId, batch, now);
  beginManualRefresh(orgId, batch);

  const scope = actAsCacheKey();
  const { refresh, queryClient, applyRows } = deps;
  const controller = new AbortController();
  const abortTimer = setTimeout(
    () => controller.abort(),
    STORAGE_FRESHNESS_TIMEOUT_MS
  );
  refresh(orgId, batch, controller.signal, { force: true })
    .finally(() => clearTimeout(abortTimer))
    .then((response) => {
      const { enabled } = landFreshnessResponse(response, queryClient, scope);
      if (!enabled) return;
      const rows = response.rows ?? [];
      const verified = rows.filter((row) => row.verified);
      // Rows answered from the API's window: landed like verified rows (the
      // logo cache too - landFreshnessResponse skipped them), so the cart's
      // count and stamp catch up with whoever refreshed them.
      const answeredAt = Date.now();
      const recent =
        response.forced === true
          ? batch
              .map((id) => rowInsideManualWindow(rows, id, answeredAt))
              .filter((row): row is VerifyStorageRowDto => row != null)
              .map((row) => ({ ...row, verified: true }))
          : [];
      if (recent.length > 0) {
        patchStorageInQueryCache(queryClient, recent, scope);
      }
      const landed = [...verified, ...recent];
      if (landed.length > 0) {
        patchStorageInDesignQueryCache(queryClient, landed, scope);
        applyRows(landed, scope);
        landed.forEach((row) =>
          alignGuardWithStamp(
            orgId,
            row.logoId,
            row.storageUpdatedAtUtc ?? row.verifiedAtUtc
          )
        );
        return;
      }
      if (response.forced !== true) {
        pauseManualRefresh(
          "unavailable",
          Date.now() + STORAGE_FRESHNESS_REQUEST_TTL_MS
        );
      }
    })
    .catch(() =>
      pauseManualRefresh("failed", Date.now() + STORAGE_MANUAL_BACKOFF_MS)
    )
    .finally(() => endManualRefresh(orgId, batch));
  return true;
}

/**
 * The add-to-cart look-up as the cart provider uses it: hands back a
 * function that, given the items just added, fires ONE forced call for their
 * logos (a standalone logo = its id; a design = its logos, deduped) in the
 * organization the storage rows on screen belong to
 * (resolveStorageFreshnessOrgId, read at call time - so is the act-as
 * scope), and lands the answer through `applyRows`. Flag off = the function
 * returns at once: no ids collected, no call, no state, nothing marked.
 * Call budget: at most 1 HTTP call per add, 0 for an add whose logos were
 * all asked for within 5 minutes.
 */
export function useStorageRefreshOnAddToCart(
  applyRows: ApplyStorageRows
): (items: readonly CartItem[]) => void {
  const queryClient = useQueryClient();
  const { refreshStorageFreshness } = useApiClient();
  const { role, organizationId } = useProfileContext();
  return useCallback(
    (items: readonly CartItem[]) => {
      if (!enviroment.storageRefreshOnAddToCartEnabled) return;
      const orgId = resolveStorageFreshnessOrgId(
        getActAsOrgId(),
        role,
        organizationId
      );
      if (!(orgId > 0)) return;
      requestStorageRefreshOnAddToCart(
        orgId,
        collectCartLogoIds(items as CartItem[]),
        { refresh: refreshStorageFreshness, queryClient, applyRows }
      );
    },
    [role, organizationId, refreshStorageFreshness, queryClient, applyRows]
  );
}
